/**
 * API Client — Impact Core
 *
 * Axios instance pre-configured to call the SI Platform backend.
 * Handles JWT attachment, 401 auto-redirect, and response unwrapping.
 *
 * Storage keys are prefixed with `ic_` to avoid collision if the user
 * is also logged into the main SI Platform on the same browser.
 */

import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';

const STORAGE_KEYS = {
  accessToken: 'ic_access_token',
  refreshToken: 'ic_refresh_token',
} as const;

const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeout,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ─── Request Interceptor ───────────────────────────────────────

apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(STORAGE_KEYS.accessToken);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Let axios set Content-Type with boundary for FormData
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// ─── Response Interceptor ──────────────────────────────────────

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Determine if this is a login/token request — do NOT wipe session for those
      const requestUrl = error.config?.url ?? '';
      const isAuthRequest =
        requestUrl.includes('/auth/token') ||
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/refresh');

      if (!isAuthRequest) {
        // Only clear tokens for non-auth requests (i.e. genuine session expiry)
        localStorage.removeItem(STORAGE_KEYS.accessToken);
        localStorage.removeItem(STORAGE_KEYS.refreshToken);

        // Only redirect if we are not already on the login page
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  },
);

export { apiClient, STORAGE_KEYS };
